import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import {
  IT_SECURITY_QUESTIONS,
  isItSecurityComplete,
  calculateItSecurity,
  type ItSecurityAnswers,
} from './itSecurityQuestions';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  facilitationSupport?: boolean;
  successCriteria?: unknown;
  environment?: unknown;
  nda?: unknown;
  dataProtection?: unknown;
  itSecurity?: {
    answers: ItSecurityAnswers;
    totalScore: number;
    riskBand: string;
    bandTitle: string;
    bandMessage: string;
  };
};

function bandCopy(riskBand: 'HIGH' | 'MEDIUM' | 'LOW') {
  if (riskBand === 'HIGH') {
    return {
      bandTitle: 'High risk',
      bandMessage:
        'You must set up a meeting with GICSS Cyber Risk (or the local GIS responsible). A notification is sent to them.',
    };
  }
  if (riskBand === 'MEDIUM') {
    return {
      bandTitle: 'Medium risk',
      bandMessage:
        'A notification is sent to GICSS Cyber Risk (or local GIS). GIS will review the ticket.',
    };
  }
  return {
    bandTitle: 'Low risk',
    bandMessage: 'Good to go.',
  };
}

const ItSecurityAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [answers, setAnswers] = useState<ItSecurityAnswers>(
    state.itSecurity?.answers ?? {}
  );
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const complete = isItSecurityComplete(answers);
  const result = useMemo(() => calculateItSecurity(answers), [answers]);

  const setYesNo = (code: string, value: boolean) => {
    setAnswers((current) => ({ ...current, [code]: value }));
  };

  const handleNext = async () => {
    setAttemptedNext(true);
    setSubmitError(null);
    if (!complete) return;
  
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
  
    setIsSubmitting(true);
    try {
      const saved = await apiService.saveItSecurity(pocId, {
        question1: answers.ITS_Q1 === true,
        question2: answers.ITS_Q2 === true,
        question3: answers.ITS_Q3 === true,
        question4: answers.ITS_Q4 === true,
        question5: answers.ITS_Q5 === true,
        question6: answers.ITS_Q6 === true,
        question7: answers.ITS_Q7 === true,
      });
      const copy = bandCopy(saved.riskBand);
  
      navigate('/pocs/new/preparation/legal', {
        state: {
          ...state,
          itSecurity: {
            answers,
            totalScore: saved.totalScore,
            riskBand: saved.riskBand,
            bandTitle: copy.bandTitle,
            bandMessage: copy.bandMessage,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save IT security. Check Network tab (PoC must be in Preparation).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const answeredCount = IT_SECURITY_QUESTIONS.filter(
    (q) => answers[q.code] !== undefined
  ).length;

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step initiation__step--active">
            Step 5
          </span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            IT security assessment
          </Headline>
          <Lead>
            Step 5 — Answer each question with Yes or No. This assessment is
            mandatory before Preparation can continue.
          </Lead>

          <div className="scorecard">
            {IT_SECURITY_QUESTIONS.map((q) => (
              <div key={q.code} className="scorecard__row">
                <div className="scorecard__prompt">
                  <p>
                    <span className="scorecard__num">{q.number}.</span> {q.text}
                  </p>
                  {answers[q.code] === true ? (
                    <p className="scorecard__help-note" role="status">
                      {q.documentationAction}
                    </p>
                  ) : null}
                </div>
                <div
                  className="scorecard__answers"
                  role="group"
                  aria-label={q.text}
                >
                  <button
                    type="button"
                    className={
                      'scorecard__yn' +
                      (answers[q.code] === true ? ' scorecard__yn--on' : '')
                    }
                    onClick={() => setYesNo(q.code, true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={
                      'scorecard__yn' +
                      (answers[q.code] === false ? ' scorecard__yn--on' : '')
                    }
                    onClick={() => setYesNo(q.code, false)}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}
          </div>

          {complete ? (
            <div
              className={
                'score-result score-result--' +
                (result.riskBand === 'HIGH'
                  ? 'high'
                  : result.riskBand === 'MEDIUM'
                    ? 'medium'
                    : 'highest')
              }
            >
              <p className="score-result__label">Risk outcome</p>
              <p className="score-result__band">{result.bandTitle}</p>
              <p className="score-result__message">{result.bandMessage}</p>
              {result.requiredDocumentationActions.length > 0 ? (
                <ul className="phase-gate__list">
                  {result.requiredDocumentationActions.map((item) => (
                    <li key={item.code}>
                      Q{item.number}: {item.action}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <p className="scorecard__progress">
            Answered {answeredCount} of {IT_SECURITY_QUESTIONS.length}
          </p>

          {attemptedNext && !complete ? (
            <p className="bj-field__error">
              Answer all seven questions before continuing.
            </p>
          ) : null}
          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/data-protection', {
                  state: {
                    ...state,
                    itSecurity: complete
                      ? {
                          answers,
                          totalScore: result.totalScore,
                          riskBand: result.riskBand,
                          bandTitle: result.bandTitle,
                          bandMessage: result.bandMessage,
                        }
                      : state.itSecurity,
                  },
                })
              }
            >
              ← Back to data protection
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting}
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default ItSecurityAssessmentPage;
